import { useEffect, useRef, useState } from 'react';
import { ensureUserIdCookie } from '../utils/userIdCookie';

export type Sex = 'M' | 'F' | 'X';

// VerifiedPerson identity claims as delivered by the VaaS callback after the
// university PD is satisfied. Shape is FLAT (no `holder` wrapper) — mirrors the
// per-field SD-JWT disclosures emitted by IDV / Issuer Portal. Only the three
// required identity claims are guaranteed; everything else is optional and
// present only when an upstream verifier PD requests it.
export interface VerifiedPerson {
  givenNames: string;
  surname: string;
  dateOfBirth: string;

  sourceDocumentType?: string;
  assuranceLevel?: string;
  assuranceEstablishmentProvider?: string;
  identityAssuranceAttestationRef?: string;

  documentNumber?: string;
  documentExpirationDate?: string;
  documentIssueDate?: string;
  citizenshipCountry?: string;
  sex?: Sex;

  portrait?: {
    documentType?: string;
    encodedImage: string;
    mediaType: string;
  };
  selfie?: {
    documentType?: string;
    encodedImage: string;
    mediaType: string;
    createdAt?: string;
    imageSource?: string;
  };
}

export interface VerifiedPersonResponse {
  ok: true;
  userId: string;
  data: VerifiedPerson;
  /** From server session when a student ID offer returned crsCredentialId (not persisted in browser storage). */
  studentIdCrsCredentialId?: string | null;
}

export interface SessionFailureResponse {
  ok: false;
  userId: string;
  data: null;
  failureReason?: string;
  studentIdCrsCredentialId?: string | null;
}

/** Full GET /api/session JSON shape the portal consumes. */
export type PortalSessionGetResponse = VerifiedPersonResponse | SessionFailureResponse;

export type SessionPollingStatus = 'idle' | 'polling' | 'success' | 'error';

/**
 * Polls the backend /api/session endpoint until it returns ok=true with data or ok=false with failureReason.
 * - Starts when `enabled` is true.
 * - Calls `onSuccess` with the successful response and stops polling.
 * - Calls `onFailure` with failureReason when verification failed (closes modal, shows error).
 * - Returns current status and last error (if any).
 */
export function useSessionPolling(
  enabled: boolean,
  intervalMs: number,
  handlers: {
    onSuccess: (resp: VerifiedPersonResponse) => void;
    onFailure?: (failureReason: string) => void;
  },
) {
  const [status, setStatus] = useState<SessionPollingStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    // Cleanup helper
    const stop = () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    if (!enabled) {
      setStatus('idle');
      stop();
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        setStatus('polling');
        // Ensure user id cookie is set
        ensureUserIdCookie();
        const resp = await fetch('/api/session', { method: 'GET', credentials: 'include' });
        console.debug('[session-poll] response status', resp.status);
        if (!resp.ok) {
          // Not ready yet; keep polling (e.g., 404)
          return;
        }
        const json = (await resp.json()) as VerifiedPersonResponse | SessionFailureResponse | { ok?: false };
        if ('ok' in json && json.ok && (json as VerifiedPersonResponse).data) {
          console.log('[session-poll] success payload received');
          if (!cancelled) {
            setStatus('success');
            handlers.onSuccess(json as VerifiedPersonResponse);
            stop();
          }
        } else if (
          'ok' in json &&
          json.ok === false &&
          'failureReason' in json &&
          (json as SessionFailureResponse).failureReason
        ) {
          const reason = (json as SessionFailureResponse).failureReason ?? 'verification_failed';
          console.log('[session-poll] terminal failure received', reason);
          if (!cancelled) {
            setStatus('error');
            handlers.onFailure?.(reason);
            stop();
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Polling error');
          setStatus('error');
          console.log('[session-poll] error', e);
          // Keep polling on transient errors
        }
      }
    };

    // immediate call, then interval
    console.log('[session-poll] start with interval', intervalMs);
    poll();
    timerRef.current = window.setInterval(poll, intervalMs);

    return () => {
      cancelled = true;
      stop();
    };
  }, [enabled, intervalMs, handlers]);

  return { status, error } as const;
}
